import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Sparkles } from "lucide-react";
import { aiApi } from "../../api/ai.api";
import { useAuthStore } from "../../store/auth.store";
import { studentErrorMessage } from "./student-shared";
import "./student-draft.css";

const OFFICE_ROLES = ["owner", "manager", "agent"];

const when = (iso: string, timeZone?: string) =>
  new Date(iso).toLocaleString("fr-FR", {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Réponse préparée par Permio AI au dernier message de l'élève, dans la boîte du bureau. Elle se
 * prépare seule à l'ouverture du fil ; rien ne part sans que la personne la reprenne et l'envoie.
 * Quand l'élève demande de déplacer une leçon et que le planning l'accepte, la personne fait le
 * report d'un clic ; la réponse qui l'annonce arrive alors dans la saisie.
 */
export function StudentDraft({
  conversationId,
  sequence,
  replacing,
  onUse,
}: {
  conversationId: string;
  /** Message de l'élève auquel répondre ; `null` si le dernier message vient du bureau. */
  sequence: number | null;
  /** La saisie contient déjà un texte, que le brouillon remplacerait. */
  replacing: boolean;
  onUse: (body: string) => void;
}) {
  const { user, activeSchoolId } = useAuthStore();
  const queryClient = useQueryClient();
  const allowed = OFFICE_ROLES.includes(user?.role ?? "");
  // Même état que la conversation de Permio : sans abonnement ni service, aucun appel.
  const status = useQuery({
    queryKey: ["ai-status", activeSchoolId ?? "", user?.id ?? ""],
    queryFn: aiApi.status,
    enabled: allowed,
    retry: false,
    gcTime: 0,
  });
  const [hidden, setHidden] = useState<number | null>(null);
  const [checked, setChecked] = useState(false);
  // La personne écrit elle-même : le brouillon se réduit à une ligne, qu'elle peut rouvrir.
  const [expanded, setExpanded] = useState(false);
  // Un report fait depuis ce fil : la réponse est déjà dans la saisie.
  const [moved, setMoved] = useState<number | null>(null);
  // Même demande si la personne réessaie après une réponse perdue : un seul report.
  const moveRequest = useRef(crypto.randomUUID());
  const key = [
    "ai-student-draft",
    activeSchoolId,
    user?.id,
    conversationId,
    sequence,
  ];
  const draft = useQuery({
    queryKey: key,
    queryFn: () => aiApi.studentDraft(conversationId, crypto.randomUUID()),
    enabled: allowed && sequence !== null && !!status.data?.available,
    retry: false,
    gcTime: 0,
    staleTime: Infinity,
    refetchInterval: (query) =>
      query.state.data?.status === "preparing" ? 3000 : false,
  });
  const move = useMutation({
    mutationFn: (id: string) =>
      aiApi.moveStudentLesson(id, moveRequest.current),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      if (data.status !== "ready") return;
      onUse(data.body);
      setMoved(sequence);
      // Mesure seulement : un échec ne gêne pas la réponse.
      void aiApi.studentDraftUsed(data.id).catch(() => undefined);
    },
  });
  if (!draft.isEnabled || sequence === null || hidden === sequence) return null;
  if (draft.isPending || draft.data?.status === "preparing")
    return (
      <p role="status" className="student-draft student-draft--waiting">
        <Sparkles size={14} aria-hidden="true" /> Permio prépare une réponse…
      </p>
    );
  // Brouillon indisponible (budget du jour, service) : la saisie reste libre, sans alerte.
  if (draft.data?.status !== "ready") return null;
  const { id, body, move: planned } = draft.data;
  if (planned?.done && moved === sequence)
    return (
      <p role="status" className="student-draft student-draft--waiting">
        <CalendarClock size={14} aria-hidden="true" /> Leçon déplacée au{" "}
        {when(planned.to, planned.timezone)}. La réponse qui l’annonce est prête
        dans la saisie.
      </p>
    );
  const waiting = !!planned && !planned.done;
  const collapsed = replacing && !waiting && !expanded;
  return (
    <aside
      className={`student-draft${collapsed ? " student-draft--collapsed" : ""}`}
      aria-label="Réponse préparée par Permio"
    >
      <p className="student-draft-title">
        <span className="student-draft-icon" aria-hidden="true">
          <Sparkles size={13} />
        </span>
        Réponse préparée par Permio
        <span className="student-draft-sub">· à relire avant envoi</span>
        {collapsed && (
          <button
            type="button"
            className="student-draft-toggle"
            onClick={() => setExpanded(true)}
          >
            Afficher
          </button>
        )}
      </p>
      {!collapsed && planned && (
        <section className="student-draft-move" aria-label="Report de la leçon">
          <p>
            {planned.done ? "Leçon déplacée" : "Report possible au planning"} :{" "}
            <strong>{when(planned.from, planned.timezone)}</strong> →{" "}
            <strong>{when(planned.to, planned.timezone)}</strong>
            {planned.instructorName ? `, avec ${planned.instructorName}` : ""}.
          </p>
          {waiting && (
            <>
              <label className="student-draft-check">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={move.isPending}
                  onChange={(event) => setChecked(event.target.checked)}
                />
                J’ai vérifié les horaires de travail du moniteur.
              </label>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={!checked || move.isPending}
                onClick={() => move.mutate(id)}
              >
                <CalendarClock size={14} aria-hidden="true" /> Déplacer la leçon
              </button>
              {move.isError && (
                <p role="alert">
                  {studentErrorMessage(
                    move.error,
                    "Le report n’a pas pu être fait. Réessayez.",
                  )}
                </p>
              )}
            </>
          )}
        </section>
      )}
      {!collapsed && <p className="student-draft-body">{body}</p>}
      {!collapsed && waiting && (
        <p className="student-draft-note">
          Cette réponse annonce le report : elle arrive dans la saisie une fois
          la leçon déplacée.
        </p>
      )}
      <div className="student-draft-actions" hidden={collapsed}>
        {!waiting && (
          <button
            type="button"
            className="btn btn--soft btn--sm"
            onClick={() => {
              onUse(body);
              setHidden(sequence);
              // Mesure seulement : un échec ne gêne pas la réponse.
              void aiApi.studentDraftUsed(id).catch(() => undefined);
            }}
          >
            {replacing
              ? "Remplacer par ce brouillon"
              : "Reprendre ce brouillon"}
          </button>
        )}
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setHidden(sequence)}
        >
          Écarter
        </button>
      </div>
    </aside>
  );
}
